import { deepClean } from '@luna/utils';
import { createQueryString } from '@luna/router';
import type { CustomQueryPageParams, EmptyResponse } from '@luna/api';
import { createPaginationEntity, request } from '@luna/api';
import { getFeatureConfig } from '@luna/feature-config';

import {
    getBankMessageUrl,
    getBankMessagesUrl,
    isBankMessageUnread,
    removeUnreadFlagFromMessage,
} from './BankMessagesService';
import type { BankMessage, BankMessageFlag, BankMessagesResponse } from './BankMessagesTypes';

const PAGE_SIZE = 10;

const getUrl = (filterByFlags: BankMessageFlag[] | undefined, pageNumber = 0): string => {
    const params = {
        size: PAGE_SIZE,
        page: pageNumber,
        filterByFlags,
    };

    return `${getBankMessagesUrl()}${createQueryString(deepClean(params))}`;
};

interface QueryPageParams {
    filterByFlags?: BankMessageFlag[];
}

export const {
    useEntity: useBankMessage,
    useEntities: useBankMessages,
    useEntityDelete: useDeleteBankMessage,
    useEntityUpdate: useMarkBankMessageAsRead,
    entityHelpers: { invalidateAll: invalidateBankMessages },
} = createPaginationEntity({
    name: '@shared/bank-messages/messages',
    payloadCreators: {
        queryById: (id) =>
            request<BankMessage>(getBankMessageUrl(id), {
                headers: { accept: getFeatureConfig((c) => c.bankMessages.getMessageAcceptHeader) },
            }),
        queryPage: async ({
            filterByFlags,
            pageNumber,
        }: CustomQueryPageParams<QueryPageParams> = {}) => {
            const response = await request<BankMessagesResponse>(getUrl(filterByFlags, pageNumber));
            return {
                data: response.messages || [],
                pageNumber: response.pageNumber,
                pageSize: response.pageSize,
                pageCount: response.pageCount,
            };
        },
        deleteById: (id: string) => request.delete<EmptyResponse>(getBankMessageUrl(id)),
        updateById: async ({ id, changes: message }: { id: string; changes: BankMessage }) => {
            if (!isBankMessageUnread(message.flags)) {
                return message;
            }
            await request.put<EmptyResponse>(getBankMessageUrl(id), { read: true });
            return removeUnreadFlagFromMessage(message);
        },
    },
});
